import React, { useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from 'react-redux';
import {
    ROLENAME,
    CALL_REPORT_DEPARTMENTS,
    CALL_REPORT_USER_DEPARTMENT_ID_TO_FILTER,
    getCallReportRolesForDepartments,
} from '../../utils/index';
import HttpService from "../../services/httpService";

import {
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
    Grid,
    Card,
    CardContent,
    CircularProgress,
    TextField,
    Button,
    Divider,
    Chip,
    Box,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    LinearProgress,
    Tooltip,
    Collapse,
    withStyles,
} from "@material-ui/core";
import TablePagination from "@material-ui/core/TablePagination";
import { makeStyles } from "@material-ui/core/styles";
import { Autocomplete, Skeleton } from "@material-ui/lab";
import CallIcon from "@material-ui/icons/Call";
import PhoneForwardedIcon from "@material-ui/icons/PhoneForwarded";
import AccessTimeIcon from "@material-ui/icons/AccessTime";
import AssessmentIcon from "@material-ui/icons/Assessment";
import FilterListIcon from "@material-ui/icons/FilterList";
import ClearAllIcon from "@material-ui/icons/ClearAll";
import RefreshIcon from "@material-ui/icons/Refresh";
import DateRangeOutlinedIcon from "@material-ui/icons/DateRangeOutlined";
import NetworkCheckIcon from "@material-ui/icons/NetworkCheck";
import FlashOnIcon from "@material-ui/icons/FlashOn";
import ExpandLessIcon from "@material-ui/icons/ExpandLess";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import PlayArrowIcon from "@material-ui/icons/PlayArrow";
import CloseIcon from "@material-ui/icons/Close";
import GraphicEqIcon from "@material-ui/icons/GraphicEq";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import GetAppIcon from "@material-ui/icons/GetApp";
import InfoOutlinedIcon from "@material-ui/icons/InfoOutlined";
import IconButton from "@material-ui/core/IconButton";
import moment from "moment";

const BLUE_DARK = "#1565c0";
const BLUE = "#1976d2";
const BLUE_LIGHT = "#42a5f5";

// The page size sent when "All" is picked. A ceiling, not a real count - see
// the note on the rowsPerPage state for why neither -1 nor totalCount works.
const ALL_ROWS_PAGE_SIZE = 100000;

// Indeterminate bar shown inside whichever section is loading. Matches the
// heading card's gradient so the two read as the same system.
const BrandLinearProgress = withStyles(() => ({
    root: { height: 4, borderRadius: 999, backgroundColor: "rgba(25,118,210,0.12)" },
    bar: { borderRadius: 999, background: `linear-gradient(90deg, ${BLUE_DARK}, ${BLUE}, ${BLUE_LIGHT})` },
}))(LinearProgress);

// Determinate bar for connectivity %. MUI v4 has no per-instance way to colour
// the inner bar, so it is painted with currentColor and each instance sets
// `color` on the root inline.
const MeterBarBase = withStyles(() => ({
    root: { height: 6, borderRadius: 999, backgroundColor: "#e8edf5" },
    bar: { borderRadius: 999, backgroundColor: "currentColor", transition: "transform .5s ease" },
}))(LinearProgress);

// Connectivity reads better as a band than a raw number: red below 30, amber
// to 60, green above. Clamped so a malformed value can't overflow the track.
const connectivityColor = (pct) => (pct >= 60 ? "#2e7d32" : pct >= 30 ? "#ed6c02" : "#d32f2f");

// The server's definition, not a second opinion. routes/runo.js builds
// uniqueCallsConnected, totalTalkTime and connectedCallLogs with
// $gt: ["$duration", 1] - so a 1-second call is NOT connected. The dialog used
// `> 0`, which gave such a call a green "connected" pill and counted it in the
// dialog's own connected total, contradicting the Connected column on the very
// row that opened it.
const isConnectedCall = (duration) => Number(duration) > 1;

const MeterBar = ({ value, color }) => (
    <MeterBarBase
        variant="determinate"
        value={Math.max(0, Math.min(100, Number(value) || 0))}
        style={{ color }}
    />
);

const useStyles = makeStyles((theme) => ({

    // The page is a fixed-height flex column rather than a tall stack: header,
    // filters, cards and pagination keep their natural height and the table
    // takes whatever is left. Nothing below the fold, and the pagination is
    // always on screen instead of being buried under 700px of rows.
    root: {
        padding: 0,
        background: "#eef2f7",
        // NOT 100vh: this page renders inside the app shell's antd <Content>,
        // which adds 16px margin + 24px padding beneath a 64px sticky <Header>.
        // 64 + 32 + 48 = 144px of chrome has to come off the viewport for this
        // column to end exactly at the bottom of the screen rather than pushing
        // a second scrollbar onto the shell.
        height: "calc(100vh - 144px)",
        // Floor for very short windows - below this the shell scrolls, which is
        // a better failure than squeezing the table down to nothing.
        minHeight: 380,
        boxSizing: "border-box",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        // The shell drops to 12px padding on small screens, so the budget moves.
        [theme.breakpoints.down("sm")]: {
            height: "calc(100vh - 120px)",
        },
    },

    mainPaper: {
        padding: 18,
        borderRadius: 16,
        boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
        background: "#fff",
        flex: 1,
        minHeight: 0,
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
    },
    // Everything above the table holds its own height; only the table flexes.
    fixedBlock: {
        flexShrink: 0,
    },

    headingCard: {
        marginBottom: 12,
        borderRadius: 16,
        background: "linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)",
        color: "#fff",
        boxShadow: "0 4px 16px rgba(25,118,210,0.3)",
        // Soft light bloom in the top-right so the banner has some depth
        // instead of reading as a flat two-stop gradient.
        position: "relative",
        overflow: "hidden",
        "&::after": {
            content: '""',
            position: "absolute",
            top: -90,
            right: -60,
            width: 300,
            height: 300,
            borderRadius: "50%",
            background: "radial-gradient(circle, rgba(255,255,255,0.22) 0%, rgba(255,255,255,0) 70%)",
            pointerEvents: "none",
        },
        "& > *": { position: "relative", zIndex: 1 },
    },

    // ── Header controls (mirrors the BotWot WhatsApp dashboard) ──────────
    headerActions: {
        display: "flex",
        flexDirection: "column",
        alignItems: "flex-end",
        gap: 10,
    },
    headerChip: {
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
        fontSize: 11.5,
        fontWeight: 700,
        color: "#fff",
        background: "rgba(0,0,0,0.26)",
        padding: "4px 11px",
        borderRadius: 999,
        whiteSpace: "nowrap",
    },
    // A plain white pill rather than a ghost button: white-on-#42a5f5 is only
    // ~2:1, so an outline-style control dissolves into the bright end of the
    // gradient. The 1px dark ring is what defines the edge.
    refreshButton: {
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        // Deliberately mirrors MUI's contained Button metrics so this sits
        // beside Download Excel as an equal rather than a lesser control:
        // 6/16 padding + 1.75 line-height is what gives MUI its 36px height,
        // and the uppercase + letter-spacing is its label treatment.
        gap: 8,
        padding: "6px 16px",
        minWidth: 150,
        minHeight: 36,
        boxSizing: "border-box",
        borderRadius: 8,
        border: "none",
        background: "#fff",
        color: BLUE_DARK,
        fontSize: "0.875rem",
        lineHeight: 1.75,
        fontWeight: 600,
        textTransform: "uppercase",
        letterSpacing: "0.02857em",
        fontFamily: "inherit",
        cursor: "pointer",
        boxShadow: "0 0 0 1px rgba(0,0,0,0.12), 0 3px 10px rgba(0,0,0,0.2)",
        transition: "background .18s ease, transform .18s ease, box-shadow .18s ease",
        "&:hover:not(:disabled)": {
            background: "#F1F7FE",
            transform: "translateY(-1px)",
            boxShadow: "0 0 0 1px rgba(0,0,0,0.16), 0 6px 16px rgba(0,0,0,0.26)",
        },
        "&:active:not(:disabled)": { transform: "translateY(0)" },
        "&:disabled": { cursor: "wait", opacity: 0.85 },
    },
    refreshSpinning: { animation: "$spin 0.9s linear infinite" },
    "@keyframes spin": { from: { transform: "rotate(0deg)" }, to: { transform: "rotate(360deg)" } },
    "@keyframes pulseDot": {
        "0%, 100%": { opacity: 1, transform: "scale(1)" },
        "50%": { opacity: 0.45, transform: "scale(0.82)" },
    },
    // Darkened pill rather than plain white text - this sits over the bright
    // end of the gradient, where white text alone is hard to read.
    lastUpdatedText: {
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
        fontSize: 11,
        fontWeight: 600,
        color: "#fff",
        background: "rgba(0,0,0,0.42)",
        padding: "3px 10px",
        borderRadius: 999,
        whiteSpace: "nowrap",
    },
    liveDot: {
        width: 6,
        height: 6,
        borderRadius: "50%",
        flexShrink: 0,
        background: "#A6E3FF",
        animation: "$pulseDot 2s ease-in-out infinite",
    },

    headingWrapper: {
        display: "flex",
        alignItems: "center",
        gap: 12,
    },

    heading: {
        fontWeight: 700,
        color: "#fff",
    },

    // Sticky is pointless now that the page itself does not scroll - it would
    // just add a stacking context for no benefit.
    filterPaper: {
        padding: 14,
        borderRadius: 14,
        marginBottom: 12,
        background: "#f8fafc",
        border: "1px solid #e2e8f0",
    },
    // Collapsing the panel is worth ~170px, which on a 768px laptop is the
    // difference between two visible rows and seven.
    filterToggle: {
        display: "inline-flex",
        alignItems: "center",
        gap: 4,
        marginLeft: 8,
        padding: "3px 10px",
        borderRadius: 999,
        border: "1px solid #cbd5e1",
        background: "#fff",
        color: "#475569",
        fontSize: 11,
        fontWeight: 700,
        fontFamily: "inherit",
        cursor: "pointer",
        transition: "background .15s ease, border-color .15s ease",
        "&:hover": { background: "#f1f5f9", borderColor: "#94a3b8" },
    },
    // Shown in place of the controls when the panel is shut, so collapsing it
    // never hides WHICH filters are narrowing the numbers.
    collapsedSummary: {
        display: "flex",
        flexWrap: "wrap",
        alignItems: "center",
        gap: 6,
    },
    // A labelled pill, not an icon-only button: "Play" / "Close" states what
    // the control does and what pressing it again will do, which a phone icon
    // behind a tooltip never did.
    playToggle: {
        display: "inline-flex",
        alignItems: "center",
        gap: 4,
        padding: "3px 10px 3px 6px",
        borderRadius: 999,
        border: `1px solid ${BLUE}55`,
        background: "#fff",
        color: BLUE_DARK,
        fontSize: 11,
        fontWeight: 700,
        fontFamily: "inherit",
        cursor: "pointer",
        transition: "background .15s ease, border-color .15s ease",
        "&:hover": { background: "#EAF3FD", borderColor: BLUE },
    },
    playToggleActive: {
        background: BLUE_DARK,
        borderColor: BLUE_DARK,
        color: "#fff",
        "&:hover": { background: BLUE, borderColor: BLUE },
    },
    // Header strip above the inline <audio>, carrying the close affordance and
    // the one line that says the row button toggles it too.
    playerBar: {
        display: "flex",
        alignItems: "center",
        gap: 8,
        marginBottom: 6,
    },
    playerTitle: {
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
        fontSize: 11.5,
        fontWeight: 800,
        color: BLUE_DARK,
        letterSpacing: 0.2,
    },
    playerClose: {
        display: "inline-flex",
        alignItems: "center",
        gap: 4,
        marginLeft: "auto",
        padding: "3px 10px 3px 6px",
        borderRadius: 999,
        border: "1px solid #cbd5e1",
        background: "#fff",
        color: "#475569",
        fontSize: 11,
        fontWeight: 700,
        fontFamily: "inherit",
        cursor: "pointer",
        "&:hover": { background: "#f1f5f9", borderColor: "#94a3b8" },
    },
    summaryChip: {
        display: "inline-flex",
        alignItems: "center",
        gap: 5,
        fontSize: 11,
        fontWeight: 600,
        padding: "3px 10px",
        borderRadius: 999,
        background: "#fff",
        border: "1px solid #e2e8f0",
        color: "#334155",
        maxWidth: 300,
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
    },

    filterHeading: {
        display: "flex",
        alignItems: "center",
        gap: 8,
        marginBottom: 10,
        fontWeight: 600,
        color: "#334155",
    },

    // The two filter groups behave differently - dropdowns fetch on selection,
    // dates wait for Apply - and nothing on screen said so. These read as a
    // legend for the controls underneath.
    filterLegend: {
        display: "flex",
        flexWrap: "wrap",
        alignItems: "center",
        gap: 8,
        marginBottom: 16,
    },
    legendPill: {
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
        fontSize: 11,
        fontWeight: 700,
        padding: "3px 10px",
        borderRadius: 999,
        border: "1px solid transparent",
        whiteSpace: "nowrap",
    },
    legendInstant: {
        background: "#E8F5E9",
        color: "#1B5E20",
        borderColor: "#c3e6c8",
    },
    legendManual: {
        background: "#FFF4E5",
        color: "#8a4b00",
        borderColor: "#f5d9b0",
    },
    // Only shown while a typed date has not reached the server yet. The ring
    // pulses because a static highlight on a button people already ignore is
    // not enough to redirect attention back to it.
    "@keyframes pendingPulse": {
        "0%, 100%": { boxShadow: "0 3px 10px rgba(237,108,2,0.35), 0 0 0 0 rgba(237,108,2,0.45)" },
        "50%": { boxShadow: "0 3px 10px rgba(237,108,2,0.35), 0 0 0 6px rgba(237,108,2,0)" },
    },
    applyPending: {
        background: "#ed6c02 !important",
        color: "#fff !important",
        animation: "$pendingPulse 1.8s ease-in-out infinite",
    },
    pendingNote: {
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
        fontSize: 11,
        fontWeight: 700,
        color: "#8a4b00",
        background: "#FFF4E5",
        border: "1px solid #f5d9b0",
        borderRadius: 8,
        padding: "4px 9px",
        marginTop: 6,
    },

    // Reserved slot rather than a conditional element, so nothing shifts by 4px
    // each time a fetch starts.
    progressSlot: {
        height: 4,
        borderRadius: 999,
        overflow: "hidden",
        marginBottom: 12,
    },
    // Filters stay readable but visibly out of action while data loads.
    filterBlock: {
        transition: "opacity .2s ease",
    },
    card: {
        position: "relative",
        overflow: "hidden",
        borderRadius: 16,
        // Trimmed padding: four tiles at MUI's default 16/24 cost ~50px of
        // height the table needs more than they do.
        "& .MuiCardContent-root": { padding: "12px 14px" },
        "& .MuiCardContent-root:last-child": { paddingBottom: 12 },
        boxShadow: "0 3px 14px rgba(0,0,0,0.08)",
        transition: "transform .25s ease, box-shadow .25s ease",
        height: "100%",
        "&:hover": {
            transform: "translateY(-4px)",
            boxShadow: "0 12px 26px rgba(25,118,210,0.18)",
        },
    },
    // Gradient rail down the left edge, tinted per metric, so the three tiles
    // read as a set rather than three unrelated boxes.
    cardAccent: {
        position: "absolute",
        left: 0,
        top: 0,
        bottom: 0,
        width: 4,
    },

    cardContentWrapper: {
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
    },

    iconWrapper: {
        width: 44,
        height: 44,
        flexShrink: 0,
        borderRadius: "50%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: "#e3f2fd",
    },

    cardTitle: {
        fontSize: 12.5,
        color: "#64748b",
        marginBottom: 2,
        fontWeight: 600,
    },

    cardValue: {
        fontSize: 24,
        fontWeight: 700,
        color: "#0f172a",
        lineHeight: 1.25,
    },
    // One line of context under each headline number - a bare "412" does not
    // say whether that is good, or what it is spread across.
    cardSubtext: {
        fontSize: 11.5,
        color: "#94a3b8",
        fontWeight: 600,
        marginTop: 3,
    },

    // Height comes from the flex column now, not a fixed 700px - the table
    // grows to fill a tall screen and shrinks on a laptop instead of pushing
    // the pagination off the bottom.
    tableContainer: {
        marginTop: 10,
        borderRadius: 16,
        boxShadow: "0 3px 14px rgba(0,0,0,0.08)",
        flex: 1,
        minHeight: 0,
        overflow: "auto",
    },

    tableHead: {
        position: "sticky",
        top: 0,
        zIndex: 10,
    },

    headCell: {
        // `!important` because MUI's stickyHeader rule paints a plain
        // background on th and would otherwise sit over the gradient.
        background: `linear-gradient(135deg, ${BLUE_DARK} 0%, ${BLUE} 100%) !important`,
        color: "#fff",
        fontWeight: 700,
        fontSize: 11.5,
        letterSpacing: "0.06em",
        textTransform: "uppercase",
        border: "none",
        whiteSpace: "nowrap",
    },

    rowHover: {
        transition: "background .16s ease, box-shadow .16s ease",
        "&:nth-of-type(even)": { background: "#FBFCFE" },
        "&:hover": {
            background: "#EFF5FE",
            boxShadow: `inset 3px 0 0 ${BLUE_LIGHT}`,
        },
    },
    // Counts are clickable (they open the call-log dialog) - a pill says so
    // where a bare number did not.
    countPill: {
        minWidth: 46,
        padding: "3px 12px",
        borderRadius: 999,
        fontWeight: 800,
        fontSize: 13,
        lineHeight: 1.5,
        textTransform: "none",
    },
    skeletonCell: {
        height: 56,
    },
    connectivityWrap: {
        display: "inline-flex",
        flexDirection: "column",
        alignItems: "stretch",
        gap: 4,
        minWidth: 86,
    },
    connectivityValue: {
        fontSize: 12.5,
        fontWeight: 800,
    },

    // ── Call-log dialog ──────────────────────────────────────────────────
    dialogPaper: {
        borderRadius: 16,
        overflow: "hidden",
    },
    dialogHead: {
        background: `linear-gradient(135deg, ${BLUE_DARK} 0%, ${BLUE} 55%, ${BLUE_LIGHT} 100%)`,
        color: "#fff",
        padding: "16px 20px",
    },
    dialogHeadRow: {
        display: "flex",
        alignItems: "center",
        gap: 12,
    },
    dialogHeadIcon: {
        width: 42,
        height: 42,
        borderRadius: 12,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
        background: "rgba(255,255,255,0.18)",
        border: "1px solid rgba(255,255,255,0.3)",
    },
    // Darkened rather than lightened: a translucent white pill washes out
    // against the light end of the header gradient.
    dialogStat: {
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
        padding: "5px 12px",
        borderRadius: 999,
        background: "rgba(0,0,0,0.26)",
        border: "1px solid rgba(255,255,255,0.22)",
        fontSize: 12,
        fontWeight: 700,
        whiteSpace: "nowrap",
    },
    dialogStatValue: { fontSize: 13, fontWeight: 800 },
    dialogSearch: {
        minWidth: 230,
        "& .MuiOutlinedInput-root": {
            borderRadius: 999,
            background: "rgba(255,255,255,0.15)",
            color: "#fff",
            fontSize: 13,
        },
        "& .MuiOutlinedInput-notchedOutline": { borderColor: "rgba(255,255,255,0.3)" },
        "& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline": { borderColor: "rgba(255,255,255,0.55)" },
        "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "#fff" },
        "& input::placeholder": { color: "rgba(255,255,255,0.72)", opacity: 1 },
    },
    dialogDownloadBtn: {
        background: "#fff",
        color: BLUE_DARK,
        fontWeight: 700,
        borderRadius: 999,
        whiteSpace: "nowrap",
        boxShadow: "0 0 0 1px rgba(0,0,0,0.12), 0 3px 10px rgba(0,0,0,0.22)",
        "&:hover": { background: "#F2F7FE" },
    },
    logHeadCell: {
        background: "#F4F7FC !important",
        color: "#5c6779",
        fontWeight: 700,
        fontSize: 11,
        letterSpacing: "0.06em",
        textTransform: "uppercase",
        borderBottom: "1px solid #e2e8f0",
        whiteSpace: "nowrap",
    },
    logRow: {
        transition: "background .15s ease",
        "&:nth-of-type(even)": { background: "#FBFCFE" },
        "&:hover": { background: "#EFF5FE" },
    },
    // Duration doubles as the connected/not-connected signal: a 0s call never
    // connected, which is the single most useful thing in this table.
    durationPill: {
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        minWidth: 62,
        padding: "3px 10px",
        borderRadius: 999,
        fontSize: 12,
        fontWeight: 800,
    },
    monoCell: {
        fontFamily: "'Roboto Mono', 'SF Mono', Menlo, monospace",
        fontSize: 12.5,
        letterSpacing: 0.2,
        color: "#37445c",
    },

    roleBadgeSE: {
        background: "#dbeafe",
        color: "#1d4ed8",
        padding: "6px 12px",
        borderRadius: 20,
        fontSize: 12,
        fontWeight: 700,
        display: "inline-block",
    },

    // Segmented control, not a dropdown. Three options is below the threshold
    // where a menu earns its extra click, and laying them out flat is what lets
    // each one carry its own count - the thing a dropdown can never show until
    // after you have already chosen.
    asmFilterGroup: {
        display: "inline-flex",
        alignItems: "center",
        gap: 3,
        padding: 3,
        borderRadius: 999,
        background: "rgba(0,0,0,0.22)",
        border: "1px solid rgba(255,255,255,0.18)",
    },
    asmFilterBtn: {
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
        padding: "5px 13px",
        borderRadius: 999,
        border: "none",
        background: "transparent",
        color: "rgba(255,255,255,0.82)",
        fontSize: 12,
        fontWeight: 700,
        fontFamily: "inherit",
        cursor: "pointer",
        whiteSpace: "nowrap",
        transition: "background .16s ease, color .16s ease",
        "&:hover:not($asmFilterBtnActive)": {
            background: "rgba(255,255,255,0.14)",
            color: "#fff",
        },
    },
    // White pill on the dark track: the selected option has to be obvious
    // against a header that is already a gradient.
    asmFilterBtnActive: {
        background: "#fff",
        color: BLUE_DARK,
        boxShadow: "0 1px 4px rgba(0,0,0,0.25)",
    },
    asmFilterCount: {
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        minWidth: 20,
        padding: "0 6px",
        height: 17,
        borderRadius: 999,
        fontSize: 10.5,
        fontWeight: 800,
        fontVariantNumeric: "tabular-nums",
        background: "rgba(255,255,255,0.22)",
        color: "#fff",
    },
    asmFilterCountActive: {
        background: "#E3F2FD",
        color: BLUE_DARK,
    },
    roleBadgeASM: {
        background: "#dcfce7",
        color: "#15803d",
        padding: "6px 12px",
        borderRadius: 20,
        fontSize: 12,
        fontWeight: 700,
        display: "inline-block",
    },

    filterBtn: {
        height: 40,
        borderRadius: 10,
        fontWeight: 700,
        textTransform: "none",
        boxShadow: "none",
    },

    noData: {
        padding: 25,
        color: "#64748b",
        fontWeight: 500,
    },

    actionWrapper: {
        display: "flex",
        gap: 10,
        alignItems: "center",
        height: "100%",
    },

    applyBtn: {
        flex: 1,
        height: 42,
        borderRadius: 10,
        fontWeight: 700,
        textTransform: "none",
        boxShadow: "0 3px 10px rgba(25,118,210,0.25)",
    },

    clearBtn: {
        flex: 1,
        height: 42,
        borderRadius: 10,
        fontWeight: 700,
        textTransform: "none",
        border: "1px solid #d1d5db",
        color: "#334155",
        background: "#fff",

        "&:hover": {
            background: "#f8fafc",
            borderColor: "#94a3b8",
        }
    },
}));

const CallReport = () => {

    const { userDetails } = useSelector((state) => state.user);
    const classes = useStyles();
    // Local date, not toISOString() - that is UTC, so for anyone east of
    // Greenwich it caps the picker a day early after 05:30 IST.
    const today = moment().format("YYYY-MM-DD");
    const [loading, setLoading] = useState(false);
    // When the figures on screen were last pulled, so the header can say how
    // stale they are. Only set on a successful fetch - a failed refresh must
    // not make old numbers look fresh.
    const [lastUpdated, setLastUpdated] = useState(null);
    // "Updated 4 minutes ago" is only true at the instant it renders, so a slow
    // tick re-renders it. 30s is plenty - moment rounds to the minute anyway.
    const [nowTick, setNowTick] = useState(Date.now());
    useEffect(() => {
        const id = setInterval(() => setNowTick(Date.now()), 30000);
        return () => clearInterval(id);
    }, []);
    const [reportData, setReportData] = useState([]);
    const [users, setUsers] = useState([]);
    const [selectedDepartments, setSelectedDepartments] = useState([]);
    const [selectedRoles, setSelectedRoles] = useState([]);
    const [selectedUsers, setSelectedUsers] = useState([]);
    const [storeDateFilter, setPivotDateFilters] = useState({ startDate: "", endDate: "" })
    // The dates the data on screen was actually fetched with. Typing in a date
    // box only changes storeDateFilter, so comparing the two is what tells us
    // there is an edit the server has not seen yet.
    const [appliedDateFilter, setAppliedDateFilter] = useState(null);
    // Whether the filter panel is expanded. Remembered per browser because the
    // right answer depends on the screen: on a 13" laptop people want it shut
    // most of the time, on a large monitor there is room to leave it open.
    // Wrapped because storage throws in private windows.
    const [filtersOpen, setFiltersOpen] = useState(() => {
        try {
            return localStorage.getItem("callReport.filtersOpen") !== "false";
        } catch (e) {
            return true;
        }
    });
    const toggleFilters = () => {
        setFiltersOpen((prev) => {
            const next = !prev;
            try {
                localStorage.setItem("callReport.filtersOpen", String(next));
            } catch (e) {
                /* a remembered preference is a nicety, not a requirement */
            }
            return next;
        });
    };
    const [page, setPage] = useState(0);
    // "All" is stored as a large real page size rather than as -1 or as the
    // current totalCount:
    //   -1 breaks MUI's own arithmetic - it renders "1--1 of 137", because
    //      TablePagination does Math.min(count, (page + 1) * rowsPerPage) with
    //      no special case for -1.
    //   totalCount goes stale the moment a filter changes the row count, so
    //      "All" would quietly stop meaning all.
    // A fixed ceiling is always >= the real count, so the label, the page math
    // and the Next button all stay correct with no resyncing.
    const [rowsPerPage, setRowsPerPage] = useState(25);
    const [totalCount, setTotalCount] = useState(0);
    const [exportLoading, setExportLoading] = useState(false);
    const [selectedLogs, setSelectedLogs] = useState([]);
    const [selectedLogRole, setSelectedLogRole] = useState("");
    const [connectedCallFilter, setConnectedCallFilter] = useState("All");
    const [dialogTitle, setDialogTitle] = useState("");
    const [openLogsDialog, setOpenLogsDialog] = useState(false);
    // Dialog-local: free-text filter over the logs, and which row is playing
    // its recording inline (index, or null).
    const [logSearch, setLogSearch] = useState("");
    const [playingLogIndex, setPlayingLogIndex] = useState(null);

    const canSelectDepartment = userDetails?.roles?.some((role) =>
        [ROLENAME.Admin, ROLENAME.CenterHead, ROLENAME.BusinessHead].includes(
            role.name
        )
    );

    const implicitDepartment = useMemo(() => {
        const deptName =
            CALL_REPORT_USER_DEPARTMENT_ID_TO_FILTER[userDetails?.departmentId];
        return deptName ? [{ name: deptName }] : [];
    }, [userDetails?.departmentId]);

    const departmentsForRoleOptions =
        selectedDepartments.length > 0
            ? selectedDepartments
            : canSelectDepartment
                ? []
                : implicitDepartment;

    const roleOptions = useMemo(
        () => getCallReportRolesForDepartments(departmentsForRoleOptions),
        [departmentsForRoleOptions]
    );

    const isRoleFilterDisabled = canSelectDepartment
        ? selectedDepartments.length === 0
        : departmentsForRoleOptions.length === 0;

    // Monotonic counter identifying the newest in-flight report request. A ref,
    // not state, because changing it must not re-render and it has to be
    // readable by a fetch that started several renders ago.
    const fetchSeqRef = useRef(0);

    const fetchReport = async ({
        customUsers,
        customPage,
        customRowsPerPage,
        customStartDate,
        customEndDate,
        customDepartments,
        customRoles,
    } = {}) => {

        // Every fetch takes a ticket. Only the newest one is allowed to write to
        // state, so a slow earlier request can no longer overwrite the results of
        // a later one - which showed up as rows from the wrong page, or a filter
        // snapping back to the previous selection's data.
        const seq = ++fetchSeqRef.current;
        const isCurrent = () => seq === fetchSeqRef.current;

        try {

            setLoading(true);

            const usersToUse =
                customUsers !== undefined
                    ? customUsers
                    : selectedUsers;

            const departmentsToUse =
                customDepartments !== undefined
                    ? customDepartments
                    : selectedDepartments;

            const rolesToUse =
                customRoles !== undefined
                    ? customRoles
                    : selectedRoles;

            const params = {
                // `!== undefined`, not `||`, matching every other override here.
                // With `||` an intentional "" (which is exactly what Clear All
                // passes) was falsy, so it fell through to storeDateFilter - and
                // that still held the OLD dates, because setPivotDateFilters had
                // not flushed yet. Clear All therefore re-sent the dates it was
                // supposed to be clearing, and only worked on a second click.
                startDate:
                    customStartDate !== undefined
                        ? customStartDate
                        : storeDateFilter.startDate,
                endDate:
                    customEndDate !== undefined
                        ? customEndDate
                        : storeDateFilter.endDate,
                page:
                    customPage !== undefined
                        ? customPage
                        : page,
                rowsPerPage:
                    customRowsPerPage !== undefined
                        ? customRowsPerPage
                        : rowsPerPage,
                users:
                    usersToUse?.length > 0
                        ? usersToUse
                            .map(user => user._id)
                            .join(",")
                        : "",
                departments:
                    departmentsToUse?.length > 0
                        ? departmentsToUse.map((d) => d.name).join(",")
                        : "",
                roles:
                    rolesToUse?.length > 0
                        ? rolesToUse.map((r) => r.name).join(",")
                        : "",
            };

            const response = await HttpService.getCallReport(params);
            if (!isCurrent()) return;
            setReportData(response?.data?.data || []);
            setTotalCount(response?.data?.totalCount || 0);
            // Inside the try, after the assignments: a fetch that threw leaves
            // the previous timestamp alone rather than vouching for stale data.
            setLastUpdated(moment());
            // Recorded from `params`, not from state, because ANY fetch carries
            // whatever is currently in the date boxes - so changing a dropdown
            // silently applies a typed-but-unapplied date too. Tracking what was
            // actually sent keeps the "pending" hint honest in that case.
            setAppliedDateFilter({
                startDate: params.startDate || "",
                endDate: params.endDate || "",
            });
        } catch (error) {
            console.log(error);
        } finally {
            // A superseded request must not clear the spinner - the newer one it
            // was replaced by is still in flight.
            if (isCurrent()) {
                setLoading(false);
            }
        }
    };

    const downloadExcel = async () => {
        try {
            setExportLoading(true);

            // The APPLIED dates, not the typed ones. storeDateFilter holds
            // whatever is sitting in the date boxes, including an edit the user
            // has not pressed Apply on - and the page explicitly tells them that
            // edit has not loaded yet. Exporting it anyway produced a sheet for a
            // range the screen was not showing.
            const exportDates = appliedDateFilter || storeDateFilter;

            const response =
                await HttpService.getCallProductivityReportExport({
                    startDate: exportDates.startDate,
                    endDate: exportDates.endDate,
                    users:
                        selectedUsers?.map(user => user._id).join(",") || "",
                    departments:
                        selectedDepartments?.map((d) => d.name).join(",") ||
                        "",
                    roles:
                        selectedRoles?.map((r) => r.name).join(",") || "",
                    export: true
                });

            const allRecords = response.data.data || [];

            const excelData = allRecords.map((item, index) => ({
                "Sl No": index + 1,
                Name: item.userName,
                Role: item.role,
                Department: item.departmentName[0],
                "Total Calls": item.totalCallsMade,
                "Unique Connected": item.uniqueCallsConnected,
                "Connectivity %": `${item.connectivityPercentage}%`,
                "Total Talk Time": formatTalkTime(
                    item.totalTalkTime
                ),
                "Avg Talk Time": formatTalkTime(
                    item.averageTalkTime
                ),
            }));

            const worksheet =
                XLSX.utils.json_to_sheet(excelData);

            const workbook =
                XLSX.utils.book_new();

            XLSX.utils.book_append_sheet(
                workbook,
                worksheet,
                "Call Productivity Report"
            );

            const excelBuffer = XLSX.write(
                workbook,
                {
                    bookType: "xlsx",
                    type: "array",
                }
            );

            const blob = new Blob(
                [excelBuffer],
                {
                    type:
                        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8",
                }
            );

            saveAs(
                blob,
                `Call_Productivity_Report_${new Date()
                    .toISOString()
                    .slice(0, 10)}.xlsx`
            );
        } catch (error) {
            console.error(error);
        } finally {
            setExportLoading(false);
        }
    };

    const getUsersCDR = async () => {
        try {
            const response = await HttpService.getUsersCDR();
            setUsers(response?.data || []);
        } catch (error) {
            console.error("Error while fetching users", error);
        }
    };

    const handleDepartmentChange = (event, value) => {
        const allowedRoleNames = getCallReportRolesForDepartments(value).map(
            (role) => role.name
        );
        const nextRoles = selectedRoles.filter((role) =>
            allowedRoleNames.includes(role.name)
        );

        setSelectedDepartments(value);
        setSelectedRoles(nextRoles);
        setSelectedUsers([]);
        setPage(0);

        fetchReport({
            customDepartments: value,
            customRoles: nextRoles,
            customUsers: [],
            customPage: 0,
        });
    };

    const handleRoleChange = (event, value) => {
        setSelectedRoles(value);
        setSelectedUsers([]);
        setPage(0);

        fetchReport({
            customRoles: value,
            customUsers: [],
            customPage: 0,
        });
    };

    const handleUserChange = (event, value) => {
        setSelectedUsers(value);
        setPage(0);
        fetchReport({
            customUsers: value,
            customPage: 0,
        });
    };


    const storePivotDate = (e) => {
        const { name, value } = e.target;
        setPivotDateFilters(prev => {
            const next = { ...prev, [name]: value };
            // min/max stop the PICKER offering an invalid date, but a typed one
            // still reaches here. Rather than reject it, move the other end out
            // of the way - picking a start after the current end reads as "I
            // want to look at a later period", not as a mistake.
            if (next.startDate && next.endDate && next.startDate > next.endDate) {
                if (name === "startDate") {
                    next.endDate = next.startDate;
                } else {
                    next.startDate = next.endDate;
                }
            }
            return next;
        });
    };

    const handlePivotDateFilter = async () => {
        setPage(0);
        await fetchReport({
            customPage: 0
        });
    };

    useEffect(() => {
        getUsersCDR();
        fetchReport();
    }, []);

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
        fetchReport({
            customPage: newPage,
            customRowsPerPage: rowsPerPage
        });
    };

    const handleChangeRowsPerPage = (event) => {
        const value = parseInt(event.target.value, 10);

        // Handle "All". Previously this stored totalCount, which is not one of
        // the options MUI's Select is given - so after picking "All" the control
        // matched nothing and rendered BLANK (and logged an out-of-range
        // warning). With an empty report it also stored 0, making Math.ceil(0/0)
        // NaN and leaving the Next button enabled on a table with no rows.
        const updatedRowsPerPage =
            value === -1 ? ALL_ROWS_PAGE_SIZE : value;
        setRowsPerPage(updatedRowsPerPage);
        setPage(0);
        fetchReport({
            customRowsPerPage: updatedRowsPerPage,
            customPage: 0
        });
    };

    const totalCalls = reportData.reduce(
        (sum, item) => sum + item.totalCallsMade,
        0
    );

    const totalConnected = reportData.reduce(
        (sum, item) => sum + item.uniqueCallsConnected,
        0
    );

    const totalTalkTime = reportData.reduce(
        (sum, item) => sum + item.totalTalkTime,
        0
    );

    // Connectivity already exists per user in the table; the one figure nobody
    // could see was the overall rate, which is the number this report is
    // really about. Guarded against an empty report so it shows 0 rather than
    // NaN%.
    const overallConnectivity =
        totalCalls > 0 ? Math.round((totalConnected / totalCalls) * 100) : 0;
    const notConnected = Math.max(0, totalCalls - totalConnected);
    // Averaged over CONNECTED calls, not all calls - dividing by attempts that
    // never rang would understate how long a real conversation actually runs.
    const avgTalkPerConnected =
        totalConnected > 0 ? Math.round(totalTalkTime / totalConnected) : 0;
    const activeUserCount = reportData.length;

    // What is actually narrowing the report, for the collapsed panel. Filters
    // left empty are omitted rather than listed as "All" - the point is to show
    // at a glance what is cutting the numbers down.
    const activeFilterSummary = [
        selectedDepartments.length && {
            label: "Dept",
            value: selectedDepartments.map((d) => d.name).join(", "),
        },
        selectedRoles.length && {
            label: "Role",
            value: selectedRoles.map((r) => r.name).join(", "),
        },
        selectedUsers.length && {
            label: "User",
            value:
                selectedUsers.length <= 2
                    ? selectedUsers.map((u) => u.name).join(", ")
                    : `${selectedUsers[0].name} +${selectedUsers.length - 1} more`,
        },
    ].filter(Boolean);

    // True only when a date box holds something the current results were not
    // fetched with. Null until the first fetch lands, so nothing is flagged as
    // pending before there is anything to compare against.
    const hasPendingDateChange =
        !!appliedDateFilter &&
        ((storeDateFilter.startDate || "") !== appliedDateFilter.startDate ||
            (storeDateFilter.endDate || "") !== appliedDateFilter.endDate);

    // The range actually reflected in the numbers on screen. Both dates are
    // optional, so the label degrades to whatever is known.
    const appliedRangeLabel = (() => {
        // Reads the APPLIED dates, not the typed ones - the header chip
        // describes the numbers on screen, so it must not change the moment
        // someone edits a date box.
        const { startDate, endDate } = appliedDateFilter || storeDateFilter;
        if (!startDate && !endDate) return "Last 3 days";
        const from = startDate ? moment(startDate).format("DD MMM") : "Start";
        const to = endDate ? moment(endDate).format("DD MMM YYYY") : "Today";
        return `${from} — ${to}`;
    })();
    const appliedRangeDays = (() => {
        const { startDate, endDate } = appliedDateFilter || storeDateFilter;
        return startDate && endDate
            ? moment(endDate).diff(moment(startDate), "days") + 1
            : null;
    })();

    const handleClearAll = async () => {
        setSelectedUsers([]);
        setSelectedDepartments([]);
        setSelectedRoles([]);
        setPivotDateFilters({
            startDate: "",
            endDate: "",
        });
        setPage(0);
        await fetchReport({
            customUsers: [],
            customDepartments: [],
            customRoles: [],
            customStartDate: "",
            customEndDate: "",
            customPage: 0
        });
    };

    const formatTalkTime = (seconds) => {
        if (!seconds || seconds <= 0) {
            return "0s";
        }

        const mins = Math.floor(seconds / 60);

        const secs = Math.floor(seconds % 60);

        if (mins > 0 && secs > 0) {
            return `${mins}m ${secs}s`;
        }

        if (mins > 0) {
            return `${mins}m`;
        }
        return `${secs}s`;
    };

    const handleTotalCallsClick = (row) => {
        setDialogTitle(
            `${row.userName} - Total Calls (${row.totalCallsMade})`
        );

        setSelectedLogs(row.totalCallLogs || []);
        setOpenLogsDialog(true);
    };

    const handleConnectedCallsClick = (row) => {
        setDialogTitle(
            `${row.userName} - Connected Calls (${row.uniqueCallsConnected})`
        );

        setSelectedLogs(row.connectedCallLogs || []);
        setSelectedLogRole(row.role || "");
        setConnectedCallFilter("All");
        setOpenLogsDialog(true);
    };

    // Multi-selects previously collapsed to "3 Selected", which told you how
    // many you had picked but not who. This shows the first name plus a "+N"
    // overflow chip, with every name in a tooltip - the field stays narrow
    // enough for the 2-column grid while still naming what is selected.
    const renderSelectedTags = (value, getTagProps) => {
        if (!value.length) return null;
        const [first, ...rest] = value;
        const allNames = value.map((v) => v.name).join(", ");
        const firstProps = getTagProps({ index: 0 });
        return (
            <>
                <Tooltip title={allNames}>
                    <Chip
                        {...firstProps}
                        label={first.name}
                        color="primary"
                        size="small"
                        style={{ maxWidth: rest.length ? 110 : 150 }}
                    />
                </Tooltip>
                {rest.length > 0 && (
                    <Tooltip title={rest.map((v) => v.name).join(", ")}>
                        {/* Not deletable on purpose - removing "+2" would be
                            ambiguous about which two it dropped. */}
                        <Chip
                            key="overflow"
                            label={`+${rest.length}`}
                            size="small"
                            style={{ fontWeight: 700, background: "#e3f2fd", color: BLUE_DARK }}
                        />
                    </Tooltip>
                )}
            </>
        );
    };

    const handleCloseLogs = () => {
        setOpenLogsDialog(false);
        setSelectedLogs([]);
        // Reset per-dialog state, or the next user's logs open pre-filtered by
        // a term from the last one and with a stale row expanded.
        setLogSearch("");
        setPlayingLogIndex(null);
        setSelectedLogRole("");
        setConnectedCallFilter("All");
    };
    // The three ASM buckets, with their predicates declared ONCE so the counts
    // on the buttons and the rows behind them can never disagree - two copies
    // of "meeting done" would drift the first time either rule changed.
    const ASM_CALL_FILTERS = useMemo(() => [
        { value: "All", label: "All Calls", match: null },
        { value: "Meeting Done Call", label: "Meeting Done", match: (log) => log.salesMeetingDone === true },
        { value: "Quotation Call", label: "Quotation Call", match: (log) => log.currentStage === "Quotation Sent" },
    ], []);

    // Matched on the two numbers a user would actually search by. Duration and
    // timestamps are excluded - partial matches on those are meaningless.
    //
    // IMPORTANT: customerMobile is the customer's MASKED number. The aggregation
    // in routes/runo.js maps it to customerData.contact_no, which is stored
    // masked ("XXXXXXXX70"), so only the trailing two digits are real. userMobile
    // is the agent's own number and is stored in full.
    //
    // That means typing a full customer number would match nothing. So a query
    // of 2+ digits ALSO matches on its last two, letting someone paste a whole
    // number and still find the call. Those hits are necessarily approximate -
    // any customer ending in the same two digits matches - which is why the
    // hint below says so rather than leaving it to be discovered.
    const logSearchTerm = logSearch.trim().toLowerCase();
    const logSearchDigits = logSearch.replace(/\D/g, "");
    const logSearchLastTwo = logSearchDigits.length >= 2 ? logSearchDigits.slice(-2) : "";

    // Declared once, applied in two places - null when the box is empty, so
    // callers can skip the filter entirely instead of testing the term again.
    const matchesLogSearch = useMemo(() => {
        if (!logSearchTerm) return null;
        return (log) => {
            const user = String(log.userMobile || "").toLowerCase();
            const customer = String(log.customerMobile || "").toLowerCase();
            if (user.includes(logSearchTerm) || customer.includes(logSearchTerm)) return true;
            return Boolean(logSearchLastTwo) && customer.endsWith(logSearchLastTwo);
        };
    }, [logSearchTerm, logSearchLastTwo]);

    // The search applied on its own. Only used for the segment counts below.
    const searchedLogs = useMemo(
        () => (matchesLogSearch ? selectedLogs.filter(matchesLogSearch) : selectedLogs),
        [selectedLogs, matchesLogSearch]
    );

    // Counted over the SEARCHED set, because the search and the segment compose:
    // with "70" in the box, clicking "Meeting Done" yields the calls that are
    // both. Counting over selectedLogs made each chip advertise a number you
    // could not actually get.
    const asmFilterCounts = useMemo(() => {
        const out = {};
        for (const opt of ASM_CALL_FILTERS) {
            out[opt.value] = opt.match ? searchedLogs.filter(opt.match).length : searchedLogs.length;
        }
        return out;
    }, [searchedLogs, ASM_CALL_FILTERS]);

    const filteredConnectedLogs = useMemo(() => {
        // For non-ASM or All filter,
        // existing logs remain unchanged
        if (
            selectedLogRole !== "Assistant Sales Manager" ||
            connectedCallFilter === "All"
        ) {
            return selectedLogs;
        }

        const option = ASM_CALL_FILTERS.find((o) => o.value === connectedCallFilter);
        return option?.match ? selectedLogs.filter(option.match) : selectedLogs;
    }, [
        selectedLogs,
        selectedLogRole,
        connectedCallFilter,
        ASM_CALL_FILTERS
    ]);

    // What the table actually renders. Narrows filteredConnectedLogs, NOT
    // selectedLogs: the search runs on top of the ASM segment, so the two
    // compose instead of the search quietly undoing the segment.
    const filteredLogs = useMemo(
        () => (matchesLogSearch ? filteredConnectedLogs.filter(matchesLogSearch) : filteredConnectedLogs),
        [filteredConnectedLogs, matchesLogSearch]
    );

    // The inline player is keyed by ROW POSITION, not by a call id, so any
    // change to the visible set leaves it pointing at a different call's
    // recording. Close it whenever the filters move.
    useEffect(() => {
        setPlayingLogIndex(null);
    }, [logSearchTerm, connectedCallFilter, selectedLogRole]);

    // Headline figures for the dialog, computed over the FILTERED set so they
    // agree with the rows on screen rather than describing a hidden superset.
    const logStats = useMemo(() => {
        const connected = filteredLogs.filter((l) => isConnectedCall(l.duration));
        const talkTime = connected.reduce((sum, l) => sum + Number(l.duration || 0), 0);
        return {
            total: filteredLogs.length,
            connected: connected.length,
            talkTime,
            withRecording: filteredLogs.filter((l) => l.recordingUrl).length,
        };
    }, [filteredLogs]);

    const downloadCallLogsExcel = () => {
        try {
            // Declared once and used for the row shape, the column widths AND
            // the hyperlink column. Hardcoding the letter has already broken
            // this export: commenting out "Customer Mobile" moved Recording
            // from F to E, but the link code kept writing to F. Worse, F sits
            // outside the sheet's !ref, so XLSX.write dropped those cells
            // entirely - every "Download Recording" came out as dead text with
            // no link at all.
            const EXCEL_COLUMNS = [
                { key: "Sl No", width: 10 },
                { key: "User Mobile", width: 20 },
                { key: "Duration", width: 15 },
                { key: "Call Time", width: 30 },
                { key: "Recording", width: 30 },
            ];

            // The rows on screen, not every log behind the number. The footer
            // says "Showing 3 of 120" - an export that quietly returned all 120
            // would not be the list the user is looking at.
            const excelData = filteredLogs.map((log, index) => ({
                "Sl No": index + 1,
                "User Mobile": log.userMobile || "-",
                "Duration": formatTalkTime(log.duration || 0),
                "Call Time": log.createdAt
                    ? new Date(log.createdAt).toLocaleString("en-IN")
                    : "-",
                "Recording": log.recordingUrl
                    ? "Download Recording"
                    : "-"
            }));

            const worksheet = XLSX.utils.json_to_sheet(excelData, {
                header: EXCEL_COLUMNS.map((c) => c.key),
            });

            worksheet["!cols"] = EXCEL_COLUMNS.map((c) => ({ wch: c.width }));

            // Derived, never hardcoded - reordering EXCEL_COLUMNS now moves the
            // links with it, and the column is guaranteed to be inside !ref.
            const recordingCol = XLSX.utils.encode_col(
                EXCEL_COLUMNS.findIndex((c) => c.key === "Recording")
            );

            // Must walk the SAME array excelData was built from, or the row
            // offsets stop lining up and links land on the wrong calls.
            filteredLogs.forEach((log, index) => {
                if (!log.recordingUrl) return;
                worksheet[`${recordingCol}${index + 2}`] = {
                    t: "s",
                    v: "Download Recording",
                    l: {
                        Target: log.recordingUrl,
                        Tooltip: "Click to download recording"
                    }
                };
            });

            const workbook =
                XLSX.utils.book_new();

            XLSX.utils.book_append_sheet(
                workbook,
                worksheet,
                "Call Logs"
            );

            const excelBuffer = XLSX.write(
                workbook,
                {
                    bookType: "xlsx",
                    type: "array"
                }
            );

            const blob = new Blob(
                [excelBuffer],
                {
                    type:
                        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8"
                }
            );

            saveAs(
                blob,
                `${dialogTitle
                    .replace(/[^a-zA-Z0-9]/g, "_")
                    .replace(/_+/g, "_")}_Call_Logs.xlsx`
            );

        } catch (error) {
            console.error(
                "Error downloading Excel:",
                error
            );
        }
    };
    const downloadFilteredCallLogsCSV = () => {
        try {
            // filteredLogs, not filteredConnectedLogs: this button exports what
            // the table is showing, and the table honours the search box too.
            const csvData = filteredLogs.map((log, index) => ({
                "Sl No": index + 1,
                "User Mobile": log.userMobile || "-",
                "Customer Mobile": log.customerMobile || "-",
                "Duration": formatTalkTime(log.duration || 0),
                "Call Time": log.createdAt
                    ? new Date(log.createdAt).toLocaleString("en-IN")
                    : "-",
                "Call Type":
                    log.currentStage === "Quotation Sent"
                        ? "Quotation Call"
                        : log.salesMeetingDone === true
                            ? "Meeting Done Call"
                            : "-",
                "Recording": log.recordingUrl
                    ? log.recordingUrl
                    : "-"
            }));

            const worksheet = XLSX.utils.json_to_sheet(csvData);

            const csv = XLSX.utils.sheet_to_csv(worksheet);

            const blob = new Blob(
                [csv],
                {
                    type: "text/csv;charset=utf-8;"
                }
            );

            const filterName =
                connectedCallFilter === "All"
                    ? "All_Calls"
                    : connectedCallFilter.replace(/\s+/g, "_");

            const fileName = `${dialogTitle
                .replace(/[^a-zA-Z0-9]/g, "_")
                .replace(/_+/g, "_")}_${filterName}.csv`;

            saveAs(blob, fileName);

        } catch (error) {
            console.error(
                "Error downloading CSV:",
                error
            );
        }
    };

    return (
        <div className={classes.root}>
            <Paper className={classes.mainPaper}>
                <Card className={`${classes.headingCard} ${classes.fixedBlock}`}>
                    <CardContent>
                        <div
                            className={classes.headingWrapper}
                            style={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                width: "100%",
                            }}
                        >
                            <div>
                                <div
                                    style={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 12,
                                    }}
                                >
                                    <AssessmentIcon
                                        style={{ fontSize: 34 }}
                                    />

                                    <Typography
                                        variant="h5"
                                        className={classes.heading}
                                    >
                                        Call Productivity Report
                                    </Typography>
                                </div>

                                <Typography
                                    variant="body2"
                                    style={{
                                        color:
                                            "rgba(255,255,255,0.85)",
                                        marginTop: 4,
                                        fontSize: 13,
                                    }}
                                >
                                    Showing last 3 days data by
                                    default. Use date filters to
                                    view custom data.
                                </Typography>
                            </div>

                            <div className={classes.headerActions}>
                                {/* Says which range produced the numbers below,
                                    without making anyone scroll back to the
                                    filters to check. */}
                                <div className={classes.headerChip}>
                                    <DateRangeOutlinedIcon style={{ fontSize: 15 }} />
                                    {appliedRangeLabel}
                                    {appliedRangeDays && (
                                        <span style={{ opacity: 0.75, fontWeight: 600 }}>
                                            {`· ${appliedRangeDays}d`}
                                        </span>
                                    )}
                                </div>

                                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                                    <Tooltip
                                        title={
                                            lastUpdated
                                                ? `Last pulled at ${lastUpdated.format("DD MMM YYYY, hh:mm:ss A")} — click to fetch the latest figures`
                                                : "Fetch the latest figures"
                                        }
                                    >
                                        {/* A labelled button, not an icon-only one:
                                            the word "Refresh" is what makes the
                                            control findable. The span is required
                                            because a disabled button fires no mouse
                                            events, so the tooltip would die exactly
                                            when it is most wanted - mid-refresh. */}
                                        <span style={{ display: "inline-flex" }}>
                                            <button
                                                type="button"
                                                className={classes.refreshButton}
                                                onClick={() => fetchReport()}
                                                disabled={loading || exportLoading}
                                            >
                                                {/* 20px matches MUI's startIcon
                                                    size on a medium Button. */}
                                                <RefreshIcon
                                                    className={loading ? classes.refreshSpinning : ""}
                                                    style={{ fontSize: 20 }}
                                                />
                                                {loading ? "Refreshing…" : "Refresh"}
                                            </button>
                                        </span>
                                    </Tooltip>

                                    <Button
                                        variant="contained"
                                        onClick={downloadExcel}
                                        disabled={exportLoading || loading}
                                        startIcon={
                                            exportLoading ? (
                                                <CircularProgress
                                                    size={18}
                                                    color="inherit"
                                                />
                                            ) : (
                                                <GetAppIcon />
                                            )
                                        }
                                        style={{
                                            background: "#fff",
                                            color: "#1976d2",
                                            fontWeight: 600,
                                            borderRadius: 8,
                                            minWidth: 180,
                                        }}
                                    >
                                        {exportLoading
                                            ? "Exporting..."
                                            : "Download Excel"}
                                    </Button>
                                </div>

                                {lastUpdated && (
                                    <div className={classes.lastUpdatedText}>
                                        <span className={classes.liveDot} />
                                        {/* Relative beats a clock time: it answers
                                            "is this stale?" without the reader doing
                                            the arithmetic. */}
                                        {loading
                                            ? "Fetching latest…"
                                            : `Updated ${moment(lastUpdated).from(nowTick)}`}
                                    </div>
                                )}
                            </div>
                        </div>
                    </CardContent>
                </Card>

                <Paper className={`${classes.filterPaper} ${classes.fixedBlock}`} elevation={0}>
                    <Typography className={classes.filterHeading} component="div">
                        <FilterListIcon />
                        Filters
                        {activeFilterSummary.length > 0 && (
                            <span
                                style={{
                                    fontSize: 11,
                                    fontWeight: 700,
                                    color: BLUE_DARK,
                                    background: "#e3f2fd",
                                    borderRadius: 999,
                                    padding: "2px 8px",
                                }}
                            >
                                {activeFilterSummary.length} active
                            </span>
                        )}
                        <button type="button" className={classes.filterToggle} onClick={toggleFilters}>
                            {filtersOpen ? <ExpandLessIcon style={{ fontSize: 15 }} /> : <ExpandMoreIcon style={{ fontSize: 15 }} />}
                            {filtersOpen ? "Hide" : "Show"}
                        </button>
                        {loading && (
                            <Chip
                                size="small"
                                // Chip takes `icon`, not `startIcon`. The inline
                                // colour is deliberate: Chip's own icon class paints
                                // it grey, which would wash the spinner out.
                                icon={
                                    <CircularProgress
                                        size={12}
                                        thickness={6}
                                        style={{ color: BLUE_DARK, marginLeft: 8 }}
                                    />
                                }
                                label="Fetching report…"
                                style={{
                                    marginLeft: "auto",
                                    background: "#e3f2fd",
                                    color: BLUE_DARK,
                                    fontWeight: 700,
                                    fontSize: 11,
                                }}
                            />
                        )}
                    </Typography>

                    {/* Shut: a one-line recap of what is narrowing the report, so
                        collapsing the panel costs visibility of the controls but
                        never of the filters themselves. */}
                    {!filtersOpen && (
                        <div className={classes.collapsedSummary}>
                            <span className={classes.summaryChip}>
                                <DateRangeOutlinedIcon style={{ fontSize: 13, color: "#64748b" }} />
                                {appliedRangeLabel}
                            </span>
                            {activeFilterSummary.map((item) => (
                                <span key={item.label} className={classes.summaryChip}>
                                    <span style={{ color: "#94a3b8" }}>{item.label}:</span>
                                    {item.value}
                                </span>
                            ))}
                            {activeFilterSummary.length === 0 && (
                                <span style={{ fontSize: 11.5, color: "#94a3b8", fontWeight: 600 }}>
                                    No department, role or user filter applied
                                </span>
                            )}
                        </div>
                    )}

                    <Collapse in={filtersOpen} timeout={220} unmountOnExit>
                        {/* The two groups of controls below behave differently and
                            nothing said so: the dropdowns fetch the moment you pick,
                            the date boxes wait for Apply. This is the legend for
                            that, sitting directly above the controls it describes. */}
                        <div className={classes.filterLegend}>
                            <span className={`${classes.legendPill} ${classes.legendInstant}`}>
                                <FlashOnIcon style={{ fontSize: 13 }} />
                                Department, Role &amp; User load instantly
                            </span>
                            <span className={`${classes.legendPill} ${classes.legendManual}`}>
                                <DateRangeOutlinedIcon style={{ fontSize: 13 }} />
                                Dates load when you press Apply
                            </span>
                        </div>

                        {/* Reserved slot, so starting a fetch does not jog the
                            controls down by 4px. */}
                        <div className={classes.progressSlot}>{loading ? <BrandLinearProgress /> : null}</div>

                        <Grid
                            container
                            spacing={2}
                            alignItems="center"
                            className={classes.filterBlock}
                            // Each control is individually disabled too; this stops
                            // clicks landing on the gaps between them mid-fetch.
                            style={{ opacity: loading ? 0.6 : 1, pointerEvents: loading ? "none" : "auto" }}
                        >
                            {(userDetails?.roles &&
                                userDetails.roles.some(role =>
                                    [ROLENAME.Admin, ROLENAME.CenterHead, ROLENAME.BusinessHead].includes(role.name)
                                )) && (
                                    <Grid item xs={12} sm={2}>
                                        <Autocomplete
                                            multiple
                                            disabled={loading}
                                            options={CALL_REPORT_DEPARTMENTS}
                                            getOptionLabel={(option) => option.name}
                                            value={selectedDepartments}
                                            onChange={handleDepartmentChange}
                                            renderTags={renderSelectedTags}
                                            renderInput={(params) => (
                                                <TextField
                                                    {...params}
                                                    variant="outlined"
                                                    size="small"
                                                    label="Department"
                                                />
                                            )}
                                        />
                                    </Grid>
                                )}

                            {(userDetails?.roles &&
                                userDetails.roles.some(role =>
                                    [ROLENAME.Admin, ROLENAME.CenterHead, ROLENAME.BusinessHead].includes(role.name)
                                )) && (
                                    <Grid item xs={12} sm={2}>
                                        <Autocomplete
                                            multiple
                                            disabled={loading || isRoleFilterDisabled}
                                            options={roleOptions}
                                            getOptionLabel={(option) => option.name}
                                            value={selectedRoles}
                                            onChange={handleRoleChange}
                                            renderTags={renderSelectedTags}
                                            renderInput={(params) => (
                                                <TextField
                                                    {...params}
                                                    variant="outlined"
                                                    size="small"
                                                    label="Role"
                                                    placeholder={
                                                        canSelectDepartment &&
                                                            selectedDepartments.length === 0
                                                            ? "Select department first"
                                                            : ""
                                                    }
                                                />
                                            )}
                                        />
                                    </Grid>
                                )}

                            {(userDetails?.roles &&
                                !userDetails.roles.some(role =>
                                    [ROLENAME.SalesUser, ROLENAME.DesignUser].includes(role.name)
                                )) && (
                                    <Grid item xs={12} sm={2}>
                                        <Autocomplete
                                            multiple
                                            disabled={loading}
                                            options={users}
                                            getOptionLabel={(option) => option.name}
                                            value={selectedUsers}
                                            onChange={handleUserChange}
                                            renderTags={renderSelectedTags}
                                            renderInput={(params) => (
                                                <TextField
                                                    {...params}
                                                    variant="outlined"
                                                    size="small"
                                                    label="Users"
                                                />
                                            )}
                                        />
                                    </Grid>
                                )}
                            <Grid
                                item
                                xs={12}
                                sm={
                                    (userDetails?.roles &&
                                        !userDetails.roles.some(role =>
                                            [ROLENAME.SalesUser, ROLENAME.DesignUser].includes(role.name)
                                        ))
                                        ? 2
                                        : 4
                                }
                            >
                                <TextField
                                    fullWidth
                                    name="startDate"
                                    label="Start Date"
                                    disabled={loading}
                                    type="date"
                                    variant="outlined"
                                    size="small"
                                    InputLabelProps={{
                                        shrink: true,
                                    }}
                                    // Calls cannot have been made in the future,
                                    // and a start after the end is a range the
                                    // server can only answer with "no data" -
                                    // which reads as a broken report, not as a
                                    // bad input. The picker now refuses both.
                                    inputProps={{
                                        max: storeDateFilter.endDate || today,
                                    }}
                                    value={storeDateFilter.startDate}
                                    onChange={storePivotDate}
                                />
                            </Grid>

                            <Grid
                                item
                                xs={12}
                                sm={
                                    (userDetails?.roles &&
                                        !userDetails.roles.some(role =>
                                            [ROLENAME.SalesUser, ROLENAME.DesignUser].includes(role.name)
                                        ))
                                        ? 2
                                        : 4
                                }
                            >
                                <TextField
                                    fullWidth
                                    name="endDate"
                                    label="End Date"
                                    disabled={loading}
                                    type="date"
                                    variant="outlined"
                                    size="small"
                                    InputLabelProps={{
                                        shrink: true,
                                    }}
                                    inputProps={{
                                        min: storeDateFilter.startDate || undefined,
                                        max: today,
                                    }}
                                    value={storeDateFilter.endDate}
                                    onChange={storePivotDate}
                                />
                            </Grid>

                            <Grid
                                item
                                xs={12}
                                sm={
                                    (userDetails?.roles &&
                                        !userDetails.roles.some(role =>
                                            [ROLENAME.SalesUser, ROLENAME.DesignUser].includes(role.name)
                                        ))
                                        ? 2
                                        : 4
                                }
                            >
                                <div>
                                    <div className={classes.actionWrapper}>
                                        <Tooltip
                                            title={
                                                hasPendingDateChange
                                                    ? "Load the report for the dates you just picked"
                                                    : "Applies the Start and End dates above. The other filters load on their own."
                                            }
                                        >
                                            <span style={{ display: "flex", flex: 1 }}>
                                                <Button
                                                    variant="contained"
                                                    color="primary"
                                                    // Turns amber and pulses only while a typed
                                                    // date has not been sent yet, so the button
                                                    // asks for attention exactly when it needs it
                                                    // and stays quiet the rest of the time.
                                                    className={`${classes.applyBtn} ${hasPendingDateChange ? classes.applyPending : ""}`}
                                                    onClick={handlePivotDateFilter}
                                                    disabled={loading}
                                                    fullWidth
                                                    startIcon={
                                                        loading
                                                            ? <CircularProgress size={16} color="inherit" />
                                                            : <FilterListIcon />
                                                    }
                                                >
                                                    {loading ? "Loading…" : "Apply Dates"}
                                                </Button>
                                            </span>
                                        </Tooltip>

                                        <Tooltip title="Resets every filter — dates, department, role and user — and reloads">
                                            <span style={{ display: "flex", flex: 1 }}>
                                                <Button
                                                    variant="outlined"
                                                    className={classes.clearBtn}
                                                    onClick={handleClearAll}
                                                    disabled={loading}
                                                    fullWidth
                                                    startIcon={<ClearAllIcon />}
                                                >
                                                    Clear All
                                                </Button>
                                            </span>
                                        </Tooltip>
                                    </div>

                                    {/* Only appears when there is genuinely something
                                    waiting, so it never becomes background noise. */}
                                    {hasPendingDateChange && !loading && (
                                        <div className={classes.pendingNote}>
                                            <InfoOutlinedIcon style={{ fontSize: 14 }} />
                                            Date changed — press Apply Dates to load it
                                        </div>
                                    )}
                                </div>
                            </Grid>
                        </Grid>
                    </Collapse>
                </Paper>


                <Grid container spacing={2} className={classes.fixedBlock}>
                    <Grid item xs={12} sm={6} md={3}>
                        <Card className={classes.card}>
                            <div className={classes.cardAccent} style={{ background: `linear-gradient(180deg, #1976d2, #1976d266)` }} />
                            <CardContent>
                                <div className={classes.cardContentWrapper}>
                                    <div>
                                        <Typography className={classes.cardTitle}>
                                            Total Calls
                                        </Typography>

                                        {loading ? (
                                            <>
                                                <Skeleton variant="text" width={110} height={38} />
                                                <Skeleton variant="text" width={82} height={14} />
                                            </>
                                        ) : (
                                            <>
                                                <Typography className={classes.cardValue}>
                                                    {totalCalls.toLocaleString("en-IN")}
                                                </Typography>
                                                <div className={classes.cardSubtext}>
                                                    {activeUserCount
                                                        ? `across ${activeUserCount} ${activeUserCount === 1 ? "person" : "people"}`
                                                        : "no one in this selection"}
                                                </div>
                                            </>
                                        )}
                                    </div>

                                    <div className={classes.iconWrapper} style={{ background: "#1976d216" }}>
                                        <CallIcon
                                            style={{
                                                color: "#1976d2",
                                                fontSize: 30,
                                            }}
                                        />
                                    </div>
                                </div>
                            </CardContent>
                        </Card>
                    </Grid>

                    <Grid item xs={12} sm={6} md={3}>
                        <Card className={classes.card}>
                            <div className={classes.cardAccent} style={{ background: `linear-gradient(180deg, #2e7d32, #2e7d3266)` }} />
                            <CardContent>
                                <div className={classes.cardContentWrapper}>
                                    <div>
                                        <Typography className={classes.cardTitle}>
                                            Connected Calls
                                        </Typography>

                                        {loading ? (
                                            <>
                                                <Skeleton variant="text" width={110} height={38} />
                                                <Skeleton variant="text" width={82} height={14} />
                                            </>
                                        ) : (
                                            <>
                                                <Typography className={classes.cardValue}>
                                                    {totalConnected.toLocaleString("en-IN")}
                                                </Typography>
                                                <div className={classes.cardSubtext}>
                                                    {`${notConnected.toLocaleString("en-IN")} never connected`}
                                                </div>
                                            </>
                                        )}
                                    </div>

                                    <div className={classes.iconWrapper} style={{ background: "#2e7d3216" }}>
                                        <PhoneForwardedIcon
                                            style={{
                                                color: "#2e7d32",
                                                fontSize: 30,
                                            }}
                                        />
                                    </div>
                                </div>
                            </CardContent>
                        </Card>
                    </Grid>

                    {/* Connectivity existed per-person in the table but never as an
                        overall figure, which is the number this report is really
                        about. The meter reuses the same red/amber/green banding the
                        table rows use, so both read on the same scale. */}
                    <Grid item xs={12} sm={6} md={3}>
                        <Card className={classes.card}>
                            <div
                                className={classes.cardAccent}
                                style={{ background: `linear-gradient(180deg, ${connectivityColor(overallConnectivity)}, ${connectivityColor(overallConnectivity)}66)` }}
                            />
                            <CardContent>
                                <div className={classes.cardContentWrapper}>
                                    <div style={{ minWidth: 0, flex: 1 }}>
                                        <Typography className={classes.cardTitle}>
                                            Connectivity
                                        </Typography>

                                        {loading ? (
                                            <>
                                                <Skeleton variant="text" width={110} height={38} />
                                                <Skeleton variant="text" width={82} height={14} />
                                            </>
                                        ) : (
                                            <>
                                                <Typography
                                                    className={classes.cardValue}
                                                    style={{ color: connectivityColor(overallConnectivity) }}
                                                >
                                                    {overallConnectivity}%
                                                </Typography>
                                                <div style={{ maxWidth: 120, marginTop: 8 }}>
                                                    <MeterBar
                                                        value={overallConnectivity}
                                                        color={connectivityColor(overallConnectivity)}
                                                    />
                                                </div>
                                            </>
                                        )}
                                    </div>

                                    <div
                                        className={classes.iconWrapper}
                                        style={{ background: `${connectivityColor(overallConnectivity)}16` }}
                                    >
                                        <NetworkCheckIcon
                                            style={{
                                                color: connectivityColor(overallConnectivity),
                                                fontSize: 30,
                                            }}
                                        />
                                    </div>
                                </div>
                            </CardContent>
                        </Card>
                    </Grid>

                    <Grid item xs={12} sm={6} md={3}>
                        <Card className={classes.card}>
                            <div className={classes.cardAccent} style={{ background: `linear-gradient(180deg, #6c5ce7, #6c5ce766)` }} />
                            <CardContent>
                                <div className={classes.cardContentWrapper}>
                                    <div>
                                        <Typography className={classes.cardTitle}>
                                            Total Talk Time
                                        </Typography>

                                        {loading ? (
                                            <>
                                                <Skeleton variant="text" width={110} height={38} />
                                                <Skeleton variant="text" width={82} height={14} />
                                            </>
                                        ) : (
                                            <>
                                                <Typography className={classes.cardValue}>
                                                    {formatTalkTime(totalTalkTime)}
                                                </Typography>
                                                <div className={classes.cardSubtext}>
                                                    {`avg ${formatTalkTime(avgTalkPerConnected)} per connected call`}
                                                </div>
                                            </>
                                        )}
                                    </div>

                                    <div className={classes.iconWrapper} style={{ background: "#6c5ce716" }}>
                                        <AccessTimeIcon
                                            style={{
                                                color: "#6c5ce7",
                                                fontSize: 30,
                                            }}
                                        />
                                    </div>
                                </div>
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>

                {/* Sits above the table so activity is visible even when the
                    sticky filter panel has scrolled out of view. Also covers the
                    Excel export, which can run long on a wide date range. */}
                <div
                    className={`${classes.progressSlot} ${classes.fixedBlock}`}
                    style={{ marginTop: 12, marginBottom: 0 }}
                >
                    {loading || exportLoading ? <BrandLinearProgress /> : null}
                </div>

                {(loading || exportLoading) && (
                    <div
                        className={classes.fixedBlock}
                        style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 10,
                            marginTop: 10,
                            padding: "8px 12px",
                            borderRadius: 10,
                            background: "#EEF4FD",
                            border: "1px solid #cfe0f7",
                        }}
                    >
                        <CircularProgress size={17} thickness={5} style={{ color: BLUE }} />
                        <div style={{ minWidth: 0 }}>
                            <div style={{ fontSize: 13, fontWeight: 700, color: BLUE_DARK }}>
                                {exportLoading ? "Building your Excel export…" : "Fetching call report…"}
                            </div>
                            <div style={{ fontSize: 11.5, color: "#5c6779" }}>
                                {exportLoading
                                    ? "Formatting every call into the sheet — the download starts on its own once it is ready."
                                    : "Pulling every call for this selection — this can take a few seconds on a wide date range."}
                            </div>
                        </div>
                    </div>
                )}

                <TableContainer
                    component={Paper}
                    className={classes.tableContainer}
                    style={{ overflowY: "auto", marginTop: 8 }}
                >
                    <Table>
                        <TableHead className={classes.tableHead}>
                            <TableRow>
                                <TableCell className={classes.headCell}>
                                    Sl No
                                </TableCell>

                                <TableCell className={classes.headCell}>
                                    Name
                                </TableCell>

                                <TableCell className={classes.headCell}>
                                    Role
                                </TableCell>

                                <TableCell className={classes.headCell}>
                                    Department
                                </TableCell>

                                <TableCell
                                    align="center"
                                    className={classes.headCell}
                                >
                                    Total Calls
                                </TableCell>

                                <TableCell
                                    align="center"
                                    className={classes.headCell}
                                >
                                    Unique Connected
                                </TableCell>

                                <TableCell
                                    align="center"
                                    className={classes.headCell}
                                >
                                    Connectivity %
                                </TableCell>

                                <TableCell
                                    align="center"
                                    className={classes.headCell}
                                >
                                    Total Talk Time
                                </TableCell>

                                <TableCell
                                    align="center"
                                    className={classes.headCell}
                                >
                                    Avg Talk Time
                                </TableCell>
                            </TableRow>
                        </TableHead>

                        <TableBody>
                            {loading ? (
                                // Placeholder rows shaped like real ones, so the
                                // table keeps its height and column rhythm instead
                                // of collapsing to a single spinner and snapping
                                // back when the data lands.
                                Array.from({ length: 8 }).map((_, i) => (
                                    <TableRow key={i}>
                                        <TableCell align="center" className={classes.skeletonCell}>
                                            <Skeleton variant="text" width={22} style={{ margin: "0 auto" }} />
                                        </TableCell>
                                        <TableCell className={classes.skeletonCell}>
                                            <Skeleton variant="text" width="70%" />
                                        </TableCell>
                                        <TableCell className={classes.skeletonCell}>
                                            <Skeleton variant="rect" width={110} height={24} style={{ borderRadius: 20 }} />
                                        </TableCell>
                                        <TableCell align="center" className={classes.skeletonCell}>
                                            <Skeleton variant="text" width={70} style={{ margin: "0 auto" }} />
                                        </TableCell>
                                        <TableCell align="center" className={classes.skeletonCell}>
                                            <Skeleton variant="rect" width={46} height={24} style={{ borderRadius: 999, margin: "0 auto" }} />
                                        </TableCell>
                                        <TableCell align="center" className={classes.skeletonCell}>
                                            <Skeleton variant="rect" width={46} height={24} style={{ borderRadius: 999, margin: "0 auto" }} />
                                        </TableCell>
                                        <TableCell align="center" className={classes.skeletonCell}>
                                            <Skeleton variant="text" width={72} style={{ margin: "0 auto" }} />
                                            <Skeleton variant="rect" width={72} height={6} style={{ borderRadius: 999, margin: "4px auto 0" }} />
                                        </TableCell>
                                        <TableCell align="center" className={classes.skeletonCell}>
                                            <Skeleton variant="text" width={62} style={{ margin: "0 auto" }} />
                                        </TableCell>
                                        <TableCell align="center" className={classes.skeletonCell}>
                                            <Skeleton variant="text" width={62} style={{ margin: "0 auto" }} />
                                        </TableCell>
                                    </TableRow>
                                ))
                            ) : reportData.length > 0 ? (
                                reportData.map((item, index) => (
                                    <TableRow
                                        key={index}
                                        className={classes.rowHover}
                                    >
                                        <TableCell align="center">
                                            {page * rowsPerPage + index + 1}
                                        </TableCell>

                                        <TableCell>
                                            <Typography
                                                style={{ fontWeight: 600 }}
                                            >
                                                {item.userName}
                                            </Typography>
                                        </TableCell>

                                        <TableCell>
                                            <span
                                                className={
                                                    item.role === "Assistant Sales Manager"
                                                        ? classes.roleBadgeASM
                                                        : classes.roleBadgeSE
                                                }
                                            >
                                                {item.role}
                                            </span>
                                        </TableCell>

                                        <TableCell align="center">
                                            {item.departmentName[0]}
                                        </TableCell>

                                        <TableCell align="center">
                                            <Tooltip title="View every call this user made">
                                                <Button
                                                    size="small"
                                                    className={classes.countPill}
                                                    onClick={() => handleTotalCallsClick(item)}
                                                    style={{ color: BLUE_DARK, background: "#e8f1fd" }}
                                                >
                                                    {item.totalCallsMade}
                                                </Button>
                                            </Tooltip>
                                        </TableCell>

                                        <TableCell align="center">
                                            <Tooltip title="View the calls that connected">
                                                <Button
                                                    size="small"
                                                    className={classes.countPill}
                                                    onClick={() => handleConnectedCallsClick(item)}
                                                    style={{ color: "#1e7e34", background: "#e6f4ea" }}
                                                >
                                                    {item.uniqueCallsConnected}
                                                </Button>
                                            </Tooltip>
                                        </TableCell>

                                        <TableCell align="center">
                                            {/* A bar alongside the number - a column of
                                                bare percentages is hard to scan for the
                                                users who are struggling. */}
                                            <div className={classes.connectivityWrap}>
                                                <span
                                                    className={classes.connectivityValue}
                                                    style={{ color: connectivityColor(Number(item.connectivityPercentage)) }}
                                                >
                                                    {item.connectivityPercentage}%
                                                </span>
                                                <MeterBar
                                                    value={item.connectivityPercentage}
                                                    color={connectivityColor(Number(item.connectivityPercentage))}
                                                />
                                            </div>
                                        </TableCell>

                                        <TableCell align="center">
                                            {formatTalkTime(item.totalTalkTime)}
                                        </TableCell>

                                        <TableCell align="center">
                                            {formatTalkTime(item.averageTalkTime)}
                                        </TableCell>
                                    </TableRow>
                                ))
                            ) : (
                                <TableRow>
                                    {/* 9 columns, not 7 - the old colSpan left two
                                        stray empty cells beside the message. */}
                                    <TableCell
                                        colSpan={9}
                                        align="center"
                                        className={classes.noData}
                                    >
                                        <Box py={5}>
                                            <AssessmentIcon style={{ fontSize: 38, color: "#c7d2e0" }} />
                                            <Typography style={{ marginTop: 8, fontWeight: 600, color: "#64748b" }}>
                                                No calls found for these filters
                                            </Typography>
                                            <Typography style={{ fontSize: 12.5, color: "#94a3b8", marginTop: 2 }}>
                                                Try widening the date range, or clearing a filter.
                                            </Typography>
                                        </Box>
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
                {/* Outside the table's scroll area, so it is always on screen -
                    it used to sit below 700px of rows and needed a page scroll
                    to reach. */}
                <TablePagination
                    className={classes.fixedBlock}
                    style={{ borderTop: "1px solid #e2e8f0", minHeight: 48 }}
                    component="div"
                    count={totalCount}
                    page={page}
                    onPageChange={handleChangePage}
                    rowsPerPage={rowsPerPage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                    // The numeric page size keeps MUI's label and Next-button
                    // arithmetic right, while the Select shows "All". MUI spreads
                    // SelectProps AFTER its own value, so this override holds.
                    SelectProps={{
                        value: rowsPerPage === ALL_ROWS_PAGE_SIZE ? -1 : rowsPerPage,
                        disabled: loading || exportLoading,
                    }}
                    // The only control that was still live mid-fetch. Without
                    // this, two quick Next clicks fire two requests whose
                    // responses can land out of order, leaving rows from one page
                    // numbered as another.
                    backIconButtonProps={{ disabled: loading || exportLoading || page === 0 }}
                    nextIconButtonProps={{
                        disabled:
                            loading ||
                            exportLoading ||
                            page >= Math.ceil(totalCount / rowsPerPage) - 1,
                    }}
                    rowsPerPageOptions={[
                        25,
                        50,
                        100,
                        {
                            label: 'All',
                            value: -1
                        }
                    ]}
                />
            </Paper>
            <Dialog
                open={openLogsDialog}
                onClose={handleCloseLogs}
                maxWidth="lg"
                fullWidth
                classes={{ paper: classes.dialogPaper }}
            >
                <DialogTitle disableTypography className={classes.dialogHead}>
                    <div className={classes.dialogHeadRow}>
                        <div className={classes.dialogHeadIcon}>
                            <CallIcon style={{ fontSize: 22 }} />
                        </div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                            <Typography variant="h6" style={{ color: "#fff", fontSize: 17, fontWeight: 700 }} noWrap>
                                {dialogTitle}
                            </Typography>
                            <Typography style={{ color: "rgba(255,255,255,0.82)", fontSize: 12.5, marginTop: 2 }}>
                                Every call behind this number — search, play a recording, or export the list.
                            </Typography>
                        </div>
                        <Tooltip title="Close">
                            <IconButton size="small" onClick={handleCloseLogs} style={{ color: "#fff" }}>
                                <ClearAllIcon />
                            </IconButton>
                        </Tooltip>
                    </div>

                    {/* Figures are computed over the FILTERED rows, so they never
                        describe a set the user can't see. */}
                    <div style={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 9, marginTop: 14 }}>
                        <span className={classes.dialogStat}>
                            <CallIcon style={{ fontSize: 13 }} />
                            <span className={classes.dialogStatValue}>{logStats.total}</span>
                            {/* Denominator is the ASM-filtered set, not every log:
                                "3 of 120" beside a 9-row bucket describes a set
                                the user cannot see. */}
                            {logSearchTerm ? `of ${filteredConnectedLogs.length} calls` : "calls"}
                        </span>
                        <span className={classes.dialogStat}>
                            <PhoneForwardedIcon style={{ fontSize: 13 }} />
                            <span className={classes.dialogStatValue}>{logStats.connected}</span>
                            connected
                        </span>
                        <span className={classes.dialogStat}>
                            <AccessTimeIcon style={{ fontSize: 13 }} />
                            <span className={classes.dialogStatValue}>{formatTalkTime(logStats.talkTime)}</span>
                            talk time
                        </span>

                        {/* Placeholder states the rule outright - customer numbers
                            are masked, so the last two digits are all there is to
                            search on. The tooltip carries the why. */}
                        <Tooltip
                            title={
                                "Customer numbers are masked (XXXXXXXX70), so search them by their LAST 2 DIGITS. " +
                                "Pasting a full number also works — it is matched on its last two, so other customers " +
                                "ending in the same two digits may appear. The agent's own mobile is searchable in full."
                            }
                        >
                            <TextField
                                size="small"
                                variant="outlined"
                                placeholder="Search last 2 digits, e.g. 70"
                                value={logSearch}
                                onChange={(e) => setLogSearch(e.target.value)}
                                className={classes.dialogSearch}
                                style={{ marginLeft: "auto" }}
                                InputProps={{
                                    startAdornment: (
                                        <InfoOutlinedIcon
                                            style={{ fontSize: 16, color: "rgba(255,255,255,0.75)", marginRight: 7 }}
                                        />
                                    ),
                                    endAdornment: logSearch ? (
                                        <IconButton size="small" onClick={() => setLogSearch("")} style={{ color: "rgba(255,255,255,0.85)" }}>
                                            <ClearAllIcon style={{ fontSize: 15 }} />
                                        </IconButton>
                                    ) : null,
                                }}
                            />
                        </Tooltip>

                        {/* ASM call filter. Every option is visible with its own
                            count, so you can see there are no Quotation calls
                            without selecting that option to find out. */}
                        {selectedLogRole === "Assistant Sales Manager" && (
                            <div className={classes.asmFilterGroup}>
                                {ASM_CALL_FILTERS.map((opt) => {
                                    const active = connectedCallFilter === opt.value;
                                    const count = asmFilterCounts[opt.value] ?? 0;
                                    return (
                                        <Tooltip
                                            key={opt.value}
                                            title={
                                                count === 0
                                                    ? `No ${opt.label.toLowerCase()} in this list`
                                                    : `Show ${count} ${opt.label.toLowerCase()}${count === 1 ? "" : "s"}`
                                            }
                                        >
                                            <button
                                                type="button"
                                                className={`${classes.asmFilterBtn} ${active ? classes.asmFilterBtnActive : ""}`}
                                                onClick={() => setConnectedCallFilter(opt.value)}
                                                // Dimmed, not disabled: an empty bucket is
                                                // still worth being able to select, and the
                                                // zero beside it already says it is empty.
                                                style={!active && count === 0 ? { opacity: 0.55 } : undefined}
                                            >
                                                {opt.label}
                                                <span className={`${classes.asmFilterCount} ${active ? classes.asmFilterCountActive : ""}`}>
                                                    {count}
                                                </span>
                                            </button>
                                        </Tooltip>
                                    );
                                })}
                            </div>
                        )}

                        {/* CSV - ASM ONLY */}
                        {selectedLogRole === "Assistant Sales Manager" ? (
                            <Button
                                variant="contained"
                                size="small"
                                startIcon={<GetAppIcon />}
                                onClick={downloadFilteredCallLogsCSV}
                                style={{
                                    background: "#fff",
                                    color: "#1976d2",
                                    fontWeight: 600
                                }}
                            >
                                Download CSV
                            </Button>
                        ) : (
                            <Button
                                variant="contained"
                                size="small"
                                startIcon={<GetAppIcon />}
                                onClick={downloadCallLogsExcel}
                                className={classes.dialogDownloadBtn}
                            >
                                Download Excel
                            </Button>
                        )}
                    </div>
                </DialogTitle>

                <DialogContent style={{ padding: 0 }}>
                    <Table stickyHeader size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell align="center" className={classes.logHeadCell}>Sl No</TableCell>
                                <TableCell className={classes.logHeadCell}>User Mobile</TableCell>
                                <TableCell className={classes.logHeadCell}>
                                    <Tooltip title="Stored masked for privacy — only the last 2 digits are visible, and searchable">
                                        <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
                                            Customer Mobile
                                            <InfoOutlinedIcon style={{ fontSize: 13, opacity: 0.6 }} />
                                        </span>
                                    </Tooltip>
                                </TableCell>
                                <TableCell align="center" className={classes.logHeadCell}>Duration</TableCell>
                                <TableCell align="center" className={classes.logHeadCell}>Call Time</TableCell>
                                {/* The hint lives on the header so it is readable
                                    before anyone has clicked anything - a tooltip on
                                    the button only helps people who already hovered. */}
                                <TableCell align="center" className={classes.logHeadCell}>
                                    <Tooltip title="Play opens the recording inline, just below the row — use Close player to stop it. The download icon opens the file in a new tab instead.">
                                        <span style={{ display: "inline-flex", alignItems: "center", gap: 4, cursor: "help" }}>
                                            Recording
                                            <InfoOutlinedIcon style={{ fontSize: 13, opacity: 0.8 }} />
                                        </span>
                                    </Tooltip>
                                </TableCell>
                            </TableRow>
                        </TableHead>

                        <TableBody>
                            {filteredLogs.length > 0 ? (
                                filteredLogs.map((log, index) => {
                                    const seconds = Number(log.duration) || 0;
                                    const connected = isConnectedCall(seconds);
                                    const isPlaying = playingLogIndex === index;
                                    return (
                                        <React.Fragment key={index}>
                                            <TableRow className={classes.logRow}>
                                                <TableCell align="center" style={{ color: "#94a3b8", fontWeight: 600 }}>
                                                    {index + 1}
                                                </TableCell>

                                                <TableCell className={classes.monoCell}>
                                                    {log.userMobile || "-"}
                                                </TableCell>

                                                <TableCell className={classes.monoCell}>
                                                    {log.customerMobile || "-"}
                                                </TableCell>

                                                <TableCell align="center">
                                                    {/* 0s means the call never connected - the
                                                        most useful fact in this table, and
                                                        previously rendered as a bare "0s". */}
                                                    <Tooltip title={connected ? "Call connected" : "Call did not connect"}>
                                                        <span
                                                            className={classes.durationPill}
                                                            style={
                                                                connected
                                                                    ? { background: "#e6f4ea", color: "#1e7e34" }
                                                                    : { background: "#f1f5f9", color: "#94a3b8" }
                                                            }
                                                        >
                                                            {connected ? formatTalkTime(seconds) : "Not connected"}
                                                        </span>
                                                    </Tooltip>
                                                </TableCell>

                                                <TableCell align="center">
                                                    <div style={{ fontSize: 12.5, fontWeight: 600, color: "#2b3648" }}>
                                                        {moment(log.createdAt).format("DD MMM YYYY")}
                                                    </div>
                                                    <div style={{ fontSize: 11, color: "#94a3b8" }}>
                                                        {moment(log.createdAt).format("hh:mm A")}
                                                    </div>
                                                </TableCell>

                                                <TableCell align="center">
                                                    {log.recordingUrl ? (
                                                        <div style={{ display: "inline-flex", gap: 4, alignItems: "center" }}>
                                                            {/* While open this reads "Playing", not
                                                                "Close" - the player below already has
                                                                the one Close button, and two of them a
                                                                line apart just looked like a mistake.
                                                                It still toggles off when clicked; the
                                                                tooltip is what says so. */}
                                                            <Tooltip
                                                                title={
                                                                    isPlaying
                                                                        ? "Playing below — click to stop and close the player"
                                                                        : "Play this recording right here, without leaving the report"
                                                                }
                                                            >
                                                                <button
                                                                    type="button"
                                                                    className={`${classes.playToggle} ${isPlaying ? classes.playToggleActive : ""}`}
                                                                    onClick={() => setPlayingLogIndex(isPlaying ? null : index)}
                                                                >
                                                                    {isPlaying
                                                                        ? <GraphicEqIcon style={{ fontSize: 14 }} />
                                                                        : <PlayArrowIcon style={{ fontSize: 15 }} />}
                                                                    {isPlaying ? "Playing" : "Play"}
                                                                </button>
                                                            </Tooltip>
                                                            <Tooltip title="Open / download recording">
                                                                <IconButton
                                                                    size="small"
                                                                    component="a"
                                                                    href={log.recordingUrl}
                                                                    target="_blank"
                                                                    rel="noopener noreferrer"
                                                                >
                                                                    <GetAppIcon style={{ fontSize: 18 }} color="primary" />
                                                                </IconButton>
                                                            </Tooltip>
                                                        </div>
                                                    ) : (
                                                        <span style={{ color: "#cbd5e1" }}>—</span>
                                                    )}
                                                </TableCell>
                                            </TableRow>

                                            {/* Inline player, so listening to a call no
                                                longer means leaving the report in a new tab. */}
                                            {isPlaying && log.recordingUrl && (
                                                <TableRow>
                                                    <TableCell colSpan={6} style={{ background: "#F4F7FC", padding: "10px 16px" }}>
                                                        {/* Header strip: names the call being played and
                                                            carries the single Close control. */}
                                                        <div className={classes.playerBar}>
                                                            <span className={classes.playerTitle}>
                                                                <GraphicEqIcon style={{ fontSize: 15 }} />
                                                                {`Now playing · Call ${index + 1}`}
                                                                {connected && (
                                                                    <span style={{ color: "#7c8798", fontWeight: 600 }}>
                                                                        {`· ${formatTalkTime(seconds)}`}
                                                                    </span>
                                                                )}
                                                            </span>
                                                            <Tooltip title="Stop the recording and close this player">
                                                                <button
                                                                    type="button"
                                                                    className={classes.playerClose}
                                                                    onClick={() => setPlayingLogIndex(null)}
                                                                >
                                                                    <CloseIcon style={{ fontSize: 14 }} />
                                                                    Close player
                                                                </button>
                                                            </Tooltip>
                                                        </div>
                                                        {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
                                                        <audio controls autoPlay src={log.recordingUrl} style={{ width: "100%", height: 34 }}>
                                                            Your browser does not support audio playback
                                                        </audio>
                                                    </TableCell>
                                                </TableRow>
                                            )}
                                        </React.Fragment>
                                    );
                                })
                            ) : (
                                <TableRow>
                                    <TableCell colSpan={6} align="center">
                                        <Box py={5}>
                                            <CallIcon style={{ fontSize: 36, color: "#c7d2e0" }} />
                                            <Typography style={{ marginTop: 8, fontWeight: 600, color: "#64748b" }}>
                                                {logSearchTerm ? `No calls match “${logSearch.trim()}”` : "No records found"}
                                            </Typography>
                                            {logSearchTerm && (
                                                <>
                                                    {/* The likeliest cause of a miss: someone typed
                                                        the middle of a customer number, which is
                                                        masked out and can never match. */}
                                                    <Typography style={{ fontSize: 12.5, color: "#94a3b8", marginTop: 4 }}>
                                                        Customer numbers are masked — try just the{" "}
                                                        <strong>last 2 digits</strong>.
                                                    </Typography>
                                                    <Button size="small" onClick={() => setLogSearch("")} style={{ marginTop: 6 }}>
                                                        Clear search
                                                    </Button>
                                                </>
                                            )}
                                        </Box>
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </DialogContent>

                <DialogActions style={{ borderTop: "1px solid #e2e8f0", padding: "12px 16px" }}>
                    <Typography
                        style={{
                            marginRight: "auto",
                            paddingLeft: 8,
                            fontWeight: 600,
                            fontSize: 13,
                            color: "#64748b",
                        }}
                    >
                        {logSearchTerm
                            ? `Showing ${filteredLogs.length} of ${filteredConnectedLogs.length} records`
                            : `Total Records: ${filteredConnectedLogs.length}`}
                        {logStats.withRecording > 0 && ` · ${logStats.withRecording} with a recording`}
                    </Typography>

                    <Button
                        onClick={handleCloseLogs}
                        color="primary"
                        variant="contained"
                        style={{ borderRadius: 999, fontWeight: 700, minWidth: 110 }}
                    >
                        Close
                    </Button>
                </DialogActions>
            </Dialog>
        </div>
    );
};

export default CallReport;